
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Journalist } from './types';

export const useJournalistFollow = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const toggleFollow = useCallback(async (journalistId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: 'Authentication required',
          description: 'Please sign in to follow journalists',
          variant: 'destructive'
        });
        throw new Error('Authentication required');
      }
      
      // Check if already following
      const { data: existingFollow } = await supabase
        .from('journalist_follows')
        .select('*')
        .eq('user_id', user.id)
        .eq('journalist_id', journalistId);
      
      if (existingFollow && existingFollow.length > 0) {
        // Unfollow
        const { error } = await supabase
          .from('journalist_follows')
          .delete()
          .eq('user_id', user.id)
          .eq('journalist_id', journalistId);
          
        if (error) throw error;
        
        return false; // Returning false means now unfollowed
      } else {
        // Follow
        const { error } = await supabase
          .from('journalist_follows')
          .insert({
            user_id: user.id,
            journalist_id: journalistId,
          });
          
        if (error) throw error;
        
        return true; // Returning true means now following
      }
    } catch (err) {
      console.error('Error toggling follow status:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  return { toggleFollow, isLoading, error };
};
